import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import DateAndTimeSelector from 'components/DateAndTimeSelector';
import moment from 'moment-timezone';
import {
	getNewDateProperties,
	getNextProductReturnTime,
	getTotalRentalDurationFromProducts,
} from 'common/utils/dateCalculations';
import { updateActivePartialOrder } from 'actions';
import { Duration, OrderProduct, OrderInfo, PartialOrder, PartialWithId } from 'common/types';
import { connect } from 'react-redux';
import { useTranslation } from 'services/localization';
import * as RentalSelectors from 'selectors/RentalViewSelectors';
import * as RentalViewActions from 'actions/RentalViewActions';
import { usePermissions } from 'hooks/usePermissions';

type Props = WithStyles<typeof styles>;

const TimeAndPlaceBar = (props: Props) => {
	const { classes } = props;
	const dispatch = useDispatch();
	const products = useSelector(RentalSelectors.orderProducts);
	const rentalInfo = useSelector(RentalSelectors.orderInfo)!;
	const orderDelivery = useSelector(RentalSelectors.orderDelivery);
	const openingHoursWithExceptions = useSelector(RentalSelectors.openingHoursWithExceptions);
	const startDate = rentalInfo.startDate;
	const rentalState = rentalInfo.rentalState;
	const duration = getTotalRentalDurationFromProducts(products);
	const { t } = useTranslation();
	const { hasPermission } = usePermissions();

	const handleSetStartDate = (newDate: moment.Moment) => {
		const partialOrder = getPartialOrderFromStartDateChange({
			startDate: newDate,
			keepEndTime: false,
		});
		if (partialOrder) {
			dispatch(RentalViewActions.updateActivePartialOrder(partialOrder));
		}
	};

	const getPartialOrderFromStartDateChange = (args: {
		startDate: moment.Moment;
		keepEndTime: boolean;
	}): PartialOrder | null => {
		const newStartDate = (args.startDate || startDate).toISOString();
		const newPartialProducts: PartialWithId<OrderProduct>[] = products.map((product) => {
			const { rentalDurationInSeconds, durationType, durationName } = product;
			const productDuration: Duration = {
				durationInSeconds: rentalDurationInSeconds || 0,
				durationType: durationType || '24h',
				durationName: durationName || null,
			};
			const productEndDate = product.endDate;
			const newDateProperties = getNewDateProperties(
				{
					new: {
						startDate: newStartDate || undefined,
						...(args.keepEndTime
							? { endDate: productEndDate || undefined }
							: { duration: productDuration }),
					},
					old: {
						...(args.keepEndTime
							? { duration: productDuration }
							: { endDate: productEndDate || undefined }),
					},
				},
				openingHoursWithExceptions,
			);
			const newProductDuration = newDateProperties.duration || productDuration;
			const newProductEndDate = newDateProperties.endDate || productEndDate;
			return {
				id: product.id,
				data: {
					startDate: newStartDate,
					endDate: newProductEndDate,
					durationType: newProductDuration.durationType,
					rentalDurationInSeconds: newProductDuration.durationInSeconds,
				},
			};
		});
		const updatedPartialProductIds = newPartialProducts.map((p) => p.id);
		const newFullProducts = products.map((p) => {
			const partialProductIndex = updatedPartialProductIds.indexOf(p.id);
			if (partialProductIndex !== -1) {
				return {
					...p,
					...newPartialProducts[partialProductIndex].data,
				};
			}
			return p;
		});
		const returnTimeNext = getNextProductReturnTime(newFullProducts);
		const newPartialRentalInfo: Partial<OrderInfo> = {
			startDate: newStartDate,
			returnTimeNext,
			initialStartDate: newStartDate,
		};
		return {
			rentalInfo: {
				id: rentalInfo.id,
				data: newPartialRentalInfo,
			},
			products: newPartialProducts,
		};
	};

	return (
		<div className={classes.toolBar}>
			<DateAndTimeSelector
				selectorType={{ type: 'startTime' }}
				date={startDate}
				setDate={handleSetStartDate}
				label={t('timeLocationSelector.selectStartDate', 'Select start date')}
				disabled={rentalState !== 'BOOKED' || !!orderDelivery || !hasPermission('edit_booking')}
				rightPositionedInput={!!orderDelivery}
				orderProducts={products}
				startLocationId={rentalInfo.startLocation.id}
				endLocationId={rentalInfo.endLocation.id}
				duration={duration}
				styling={{
					disableSaveOfInvalidDateSelection: true,
					showOverbookingBanner: true,
				}}
			/>
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		toolBar: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flexStart',
			marginBottom: theme.spacing(1),
		},
		icon: {
			marginLeft: 12,
			marginRight: 12,
			marginTop: 16,
		},
	});

export default withStyles(styles)(connect(null, { updateActivePartialOrder })(TimeAndPlaceBar));
